import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import {
  ChartBarIcon,
  UserGroupIcon,
  SparklesIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  LightBulbIcon,
  CurrencyDollarIcon,
  PaintBrushIcon
} from '@heroicons/react/24/outline';
import SEOMeta from "@/templates/SEOMeta";
import {SeoInternalLinks} from "@/components/SeoInternalLinks";
import { getI18nProps } from '@/utils/i18n';

export default function AffiliateMarketing() {
  const router = useRouter();
  const t = useTranslations('affiliateMarketing');

  const stats = [
    { value: t('stats.fasterSetup.value'), label: t('stats.fasterSetup.label') },
    { value: t('stats.higherRetention.value'), label: t('stats.higherRetention.label') },
    { value: t('stats.timeSaved.value'), label: t('stats.timeSaved.label') }
  ];

  const steps = [
    {
      icon: ArrowPathIcon,
      title: t('steps.setup.title'),
      description: t('steps.setup.description')
    },
    {
      icon: UserGroupIcon,
      title: t('steps.addAffiliates.title'),
      description: t('steps.addAffiliates.description')
    },
    {
      icon: SparklesIcon,
      title: t('steps.getPromoted.title'),
      description: t('steps.getPromoted.description')
    }
  ];

  const features = [
    {
      icon: ShieldCheckIcon,
      title: t('features.fullControl.title'),
      description: t('features.fullControl.description'),
      stat: t('features.fullControl.stat')
    },
    {
      icon: LightBulbIcon,
      title: t('features.engagementTools.title'),
      description: t('features.engagementTools.description'),
      stat: t('features.engagementTools.stat')
    },
    {
      icon: ChartBarIcon,
      title: t('features.advancedData.title'),
      description: t('features.advancedData.description'),
      stat: t('features.advancedData.stat')
    },
    {
      icon: CurrencyDollarIcon,
      title: t('features.flexibleRewards.title'),
      description: t('features.flexibleRewards.description'),
      stat: t('features.flexibleRewards.stat')
    }
  ];

  const advantages = [
    {
      icon: DevicePhoneMobileIcon,
      title: t('advantages.crossDevice.title'),
      description: t('advantages.crossDevice.description')
    },
    {
      icon: GlobeAltIcon,
      title: t('advantages.globalMarketplace.title'),
      description: t('advantages.globalMarketplace.description')
    },
    {
      icon: PaintBrushIcon,
      title: t('advantages.brandedExperience.title'),
      description: t('advantages.brandedExperience.description')
    }
  ];

  return (
      <>
        <SEOMeta
            title={t('seo.title')}
            description={t('seo.description')}
            keywords={t('seo.keywords')}
            url="/affiliate-marketing"
            img="/og.png"
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero Section */}
          <section className="pt-28 pb-24 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent mb-6"
              >
                {t('hero.title')}
              </motion.h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
                {t('hero.subtitle')}
              </p>
              <Button
                  onClick={() => router.push('/signup')}
                  className="bg-gradient-to-r from-blue-600 to-green-500 text-white px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
              >
                {t('hero.cta')}
              </Button>
            </div>
          </section>

          {/* Image Section */}
          <section className="py-24">
            <div className="mx-auto justify-center">
              <div className="rounded-xl w-full h-96 flex items-center justify-center">
                <img
                    src='/affiliate-marketing.png'
                    alt="Affiliate Marketing Image"
                />
              </div>
            </div>
          </section>

          {/* Why Choose Afivio */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('whyChoose.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('whyChoose.subtitle')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 p-8 rounded-2xl"
                >
                  <div className="flex items-center mb-4">
                    <ShieldCheckIcon className="w-8 h-8 text-green-500 mr-3" />
                    <h3 className="text-xl font-semibold">{t('whyChoose.cards.simplifiedManagement.title')}</h3>
                  </div>
                  <p className="text-gray-600">
                    {t('whyChoose.cards.simplifiedManagement.description')}
                  </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gray-50 p-8 rounded-2xl"
                >
                  <div className="flex items-center mb-4">
                    <ArrowPathIcon className="w-8 h-8 text-blue-500 mr-3" />
                    <h3 className="text-xl font-semibold">{t('whyChoose.cards.automatedDataSync.title')}</h3>
                  </div>
                  <p className="text-gray-600">
                    {t('whyChoose.cards.automatedDataSync.description')}
                  </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-gray-50 p-8 rounded-2xl"
                >
                  <div className="flex items-center mb-4">
                    <CurrencyDollarIcon className="w-8 h-8 text-yellow-500 mr-3" />
                    <h3 className="text-xl font-semibold">{t('whyChoose.cards.transparentPricing.title')}</h3>
                  </div>
                  <p className="text-gray-600">
                    {t('whyChoose.cards.transparentPricing.description')}
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

                 {/* Steps */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('stepsSection.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('stepsSection.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {steps.map((step, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.2 }}
                        className="bg-white p-8 rounded-2xl shadow-sm text-center"
                    >
                      <div className="flex justify-center mb-6">
                        <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center">
                          <step.icon className="w-10 h-10 text-blue-600" />
                        </div>
                      </div>
                      <div className="bg-gray-100 inline-block px-3 py-1 rounded-full text-sm font-medium mb-4">
                        {t('stepsSection.step')} {idx + 1}
                      </div>
                      <h3 className="text-2xl font-semibold mb-2">{step.title}</h3>
                      <p className="text-gray-600">{step.description}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('featuresSection.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('featuresSection.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-12">
                {features.map((feature, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex"
                    >
                      <div className="mr-6">
                        <feature.icon className="w-12 h-12 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-semibold mb-3">{feature.title}</h3>
                        <p className="text-gray-600 mb-4">{feature.description}</p>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                          <p className="text-blue-700 italic">{feature.stat}</p>
                        </div>
                      </div>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonial */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <div className="space-y-6">
                {[1, 2, 3, 4].map((num) => (
                    <p key={num} className="text-xl leading-relaxed">
                      {t(`testimonial.paragraph${num}`)}
                    </p>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section className="py-24">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.95 }}
                  whileInView={{ scale: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white"
              >
                <h2 className="text-4xl font-bold mb-6">{t('finalCta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">
                  {t('finalCta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                      gray
                      onClick={() => router.push('/signup')}
                  >
                    {t('finalCta.cta')}
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>
          <SeoInternalLinks />
        </div>
      </>
  );
}

export const getStaticProps = getI18nProps;